import type { CUSTOMER_INFO_MESSAGE } from '~/types/Journey.types';

export const ORIGIN_DATE_SHIFT_STORAGE_KEY = 'byway.originDateShift';

export type OriginDateShiftNoticePayload = {
  journeyId: string;
  messages: CUSTOMER_INFO_MESSAGE[];
};

const KNOWN_CUSTOMER_INFO_MESSAGES: ReadonlySet<string> = new Set<CUSTOMER_INFO_MESSAGE>([
  'DEPARTURE_DATE_CHANGED_FOR_ORIGIN'
]);

export function toCustomerInfoMessages(messages: unknown): CUSTOMER_INFO_MESSAGE[] {
  if (!Array.isArray(messages)) {
    return [];
  }

  return messages.filter((message): message is CUSTOMER_INFO_MESSAGE => KNOWN_CUSTOMER_INFO_MESSAGES.has(message));
}

function isNoticePayload(value: unknown): value is OriginDateShiftNoticePayload {
  const payload = value as OriginDateShiftNoticePayload | null;

  return typeof payload?.journeyId === 'string' && Array.isArray(payload.messages);
}

function clear(): void {
  try {
    globalThis.sessionStorage.removeItem(ORIGIN_DATE_SHIFT_STORAGE_KEY);
  } catch {}
}

export function writeOriginDateShiftNotice(journeyId: string, messages: unknown): void {
  if (globalThis.window === undefined) return;

  const recognised = toCustomerInfoMessages(messages);
  if (!journeyId || recognised.length === 0) return;

  try {
    globalThis.sessionStorage.setItem(
      ORIGIN_DATE_SHIFT_STORAGE_KEY,
      JSON.stringify({ journeyId, messages: recognised } satisfies OriginDateShiftNoticePayload)
    );
  } catch {}
}

export function consumeOriginDateShiftNotice(journeyId: string): OriginDateShiftNoticePayload | null {
  if (globalThis.window === undefined) return null;

  let stored: string | null;
  try {
    stored = globalThis.sessionStorage.getItem(ORIGIN_DATE_SHIFT_STORAGE_KEY);
  } catch {
    return null;
  }

  if (stored === null) return null;

  clear();

  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return null;
  }

  if (!isNoticePayload(parsed) || parsed.journeyId !== journeyId) return null;

  const messages = toCustomerInfoMessages(parsed.messages);
  if (messages.length === 0) return null;

  return { journeyId: parsed.journeyId, messages };
}
